import React, { useRef, useState } from "react";
import {
  Box,
  Button,
  ButtonGroup,
  Chip,
  ClickAwayListener,
  Grow,
  MenuItem,
  MenuList,
  Paper,
  Popper
} from "@mui/material";
import DoneIcon from "@mui/icons-material/Done";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";

import { GlassOrderStatus } from "../../../enum/GlassOrder";
import { GlassOrder } from "../../../interfaces/GlassOrder";
import { useUpdateGlassOrderStatus } from "../../../hooks/query/useGlassOrders";
import CalendarDropdown from "../../../components/CalendarDropdown/CalendarDropdown";
import { tl } from "../../../components/translate";

export interface GlassOrderStatusChipProps {
  status: GlassOrderStatus | string;
}

const STATUS_CONFIG: Record<string, { label: string; bg: string; color: string; border: string }> =
  {
    [GlassOrderStatus.PENDING]: {
      label: "Pending",
      bg: "#fff8e1",
      color: "#b78103",
      border: "#ffe082"
    },
    [GlassOrderStatus.IN_PROGRESS]: {
      label: "In Progress",
      bg: "#e3f2fd",
      color: "#0288d1",
      border: "#90caf9"
    },
    [GlassOrderStatus.COMPLETED]: {
      label: "Completed",
      bg: "#e8f5e9",
      color: "#2e7d32",
      border: "#a5d6a7"
    },
    [GlassOrderStatus.DELIVERED]: {
      label: "Delivered",
      bg: "#e0f2f1",
      color: "#00796b",
      border: "#80cbc4"
    },
    [GlassOrderStatus.CANCELLED]: {
      label: "Cancelled",
      bg: "#ffebee",
      color: "#c62828",
      border: "#ef9a9a"
    }
  };

export const GlassOrderStatusChip: React.FC<GlassOrderStatusChipProps> = ({ status }) => {
  const config = STATUS_CONFIG[status] || {
    label: status,
    bg: "#f5f5f5",
    color: "#616161",
    border: "#e0e0e0"
  };

  return (
    <Chip
      size="small"
      label={config.label}
      sx={{
        backgroundColor: config.bg,
        color: config.color,
        border: `1px solid ${config.border}`,
        fontWeight: 600,
        fontSize: "0.75rem",
        height: 24
      }}
    />
  );
};

interface GlassOrderStatusActionProps {
  glassOrder: GlassOrder;
  onStatusUpdated?: (updatedOrder: GlassOrder) => void;
}

interface ActionOption {
  status: GlassOrderStatus;
  label: string;
}

const ALL_ACTIONS: ActionOption[] = [
  { status: GlassOrderStatus.IN_PROGRESS, label: "Mark In Progress" },
  { status: GlassOrderStatus.COMPLETED, label: "Mark Completed" },
  { status: GlassOrderStatus.DELIVERED, label: "Mark Delivered" },
  { status: GlassOrderStatus.PENDING, label: "Mark Pending" },
  { status: GlassOrderStatus.CANCELLED, label: "Mark Cancelled" }
];

export default function GlassOrderStatusAction({
  glassOrder,
  onStatusUpdated
}: GlassOrderStatusActionProps): React.JSX.Element {
  const anchorRef = useRef<HTMLDivElement>(null);
  const [openMenu, setOpenMenu] = useState(false);
  const [openDeliveryDateSelect, setOpenDeliveryDateSelect] = useState(false);
  const [deliveryDate, setDeliveryDate] = useState<string>(
    glassOrder.deliveryDate || new Date().toISOString()
  );

  const updateStatusMutation = useUpdateGlassOrderStatus();

  const availableActions = ALL_ACTIONS.filter((action) => action.status !== glassOrder.status);

  const handleClose = (event: MouseEvent | TouchEvent) => {
    if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
      return;
    }
    setOpenMenu(false);
  };

  const handleAction = async (action: ActionOption) => {
    setOpenMenu(false);

    if (action.status === GlassOrderStatus.DELIVERED) {
      setOpenDeliveryDateSelect(true);
      return;
    }

    const res = await updateStatusMutation.mutateAsync({
      id: glassOrder.id,
      payload: { status: action.status }
    });

    if (res?.data && onStatusUpdated) {
      onStatusUpdated(res.data);
    }
  };

  const handleConfirmDelivered = async () => {
    setOpenDeliveryDateSelect(false);
    const res = await updateStatusMutation.mutateAsync({
      id: glassOrder.id,
      payload: {
        status: GlassOrderStatus.DELIVERED,
        deliveryDate
      }
    });

    if (res?.data && onStatusUpdated) {
      onStatusUpdated(res.data);
    }
  };

  if (!availableActions.length) {
    return <GlassOrderStatusChip status={glassOrder.status} />;
  }

  const primaryAction = availableActions[0];

  return (
    <>
      <ButtonGroup
        size="small"
        variant="outlined"
        color="primary"
        ref={anchorRef}
        aria-label="glass order status actions"
      >
        <Button
          disabled={updateStatusMutation.isLoading}
          onClick={() => handleAction(primaryAction)}
        >
          {primaryAction.label}
        </Button>
        {availableActions.length > 1 && (
          <Button
            size="small"
            color="primary"
            disabled={updateStatusMutation.isLoading}
            onClick={() => setOpenMenu((prev) => !prev)}
          >
            <ArrowDropDownIcon />
          </Button>
        )}
      </ButtonGroup>

      <Popper
        style={{ zIndex: 1300 }}
        open={openMenu}
        anchorEl={anchorRef.current}
        transition
        disablePortal
      >
        {({ TransitionProps, placement }) => (
          <Grow
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...TransitionProps}
            style={{
              transformOrigin: placement === "bottom" ? "center top" : "center bottom"
            }}
          >
            <Paper elevation={4}>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList>
                  {availableActions.map((action, index) => {
                    if (index === 0) return null;
                    return (
                      <MenuItem key={action.status} onClick={() => handleAction(action)}>
                        {action.label}
                      </MenuItem>
                    );
                  })}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>

      <Popper
        style={{ zIndex: 1300 }}
        open={openDeliveryDateSelect}
        anchorEl={anchorRef.current}
        transition
        disablePortal
      >
        {({ TransitionProps, placement }) => (
          <Grow
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...TransitionProps}
            style={{
              transformOrigin: placement === "bottom" ? "center top" : "center bottom"
            }}
          >
            <Paper
              elevation={4}
              sx={{
                p: 2,
                border: "1px solid",
                borderColor: "divider",
                minWidth: 280
              }}
            >
              <ClickAwayListener onClickAway={() => setOpenDeliveryDateSelect(false)}>
                <Box>
                  <CalendarDropdown
                    fullwidth
                    TextFieldProps={{ variant: "outlined", size: "small" }}
                    label="Delivery Date"
                    placeholder="Select delivery date"
                    date={new Date(deliveryDate)}
                    onChange={(val) => {
                      if (val) setDeliveryDate(val.toISOString());
                    }}
                  />
                  <Box mt={2} display="flex" justifyContent="flex-end" gap={1}>
                    <Button size="small" onClick={() => setOpenDeliveryDateSelect(false)}>
                      {tl("booking.cancel")}
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      color="primary"
                      disabled={updateStatusMutation.isLoading}
                      onClick={handleConfirmDelivered}
                      endIcon={<DoneIcon />}
                    >
                      Mark Delivered
                    </Button>
                  </Box>
                </Box>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  );
}
